/**
 * Felállások a mérkőzés-szerkesztőhöz. A pályán a koordináták 0-100 között
 * értendők: X balról jobbra, Y az ellenfél kapujától (0) a saját kapuig (100),
 * vagyis a csapat felfelé támad.
 */

export interface PitchSlot {
  id: string;
  x: number;
  y: number;
  /** Poszt rövidítés, pl. "CB". */
  role: string;
  playerId?: number;
}

/** Létszámonként a választható felállások (a kapus nélkül). */
export const FORMATIONS: Record<number, string[]> = {
  11: ['4-4-2', '4-3-3', '4-2-3-1', '4-1-4-1', '3-5-2', '3-4-3', '5-3-2'],
  9: ['3-3-2', '3-2-3', '4-3-1', '3-4-1'],
  8: ['3-3-1', '2-3-2', '3-2-2'],
  7: ['2-3-1', '3-2-1', '2-1-2-1', '3-1-2'],
  6: ['2-2-1', '2-1-2', '3-1-1'],
  5: ['2-2', '1-2-1', '2-1-1'],
};

export const TEAM_SIZES = [11, 9, 8, 7, 6, 5];

/** Futsal: 4 mezőnyjátékos + kapus - ezzel nyílik a felállás, ha még nincs mentve. */
export const FUTSAL_FORMATION = '2-2';

/** A meccs felállása; ha még nincs mentve, futsalnál a 4+1-es alap. */
export const matchFormation = (match: { formation?: string | null; isFutsal?: boolean }): string | undefined =>
  match.formation || (match.isFutsal ? FUTSAL_FORMATION : undefined);

const DEFAULT_FORMATION: Record<number, string> = {
  11: '4-4-2',
  9: '3-3-2',
  8: '3-3-1',
  7: '2-3-1',
  6: '2-2-1',
  5: '2-2',
};

export const ROLE_LABELS: Record<string, string> = {
  GK: 'Kapus',
  LB: 'Bal hátvéd',
  CB: 'Középhátvéd',
  RB: 'Jobb hátvéd',
  LWB: 'Bal szárnyvédő',
  RWB: 'Jobb szárnyvédő',
  CDM: 'Védekező középpályás',
  CM: 'Középpályás',
  LM: 'Bal középpályás',
  RM: 'Jobb középpályás',
  CAM: 'Támadó középpályás',
  LW: 'Bal szélső',
  RW: 'Jobb szélső',
  ST: 'Csatár',
};

const parseLines = (formation: string): number[] | null => {
  const lines = formation.split('-').map((n) => parseInt(n, 10));
  return lines.length >= 2 && lines.every((n) => n >= 1 && n <= 6) ? lines : null;
};

/** A felállás létszáma a kapussal együtt; ismeretlen felállásnál null. */
export const formationSize = (formation?: string | null): number | null => {
  const lines = formation ? parseLines(formation) : null;
  return lines ? lines.reduce((a, b) => a + b, 0) + 1 : null;
};

/** A kezdők számához legjobban illő alap felállás. */
export const defaultFormationFor = (starters: number): string => {
  if (starters === 0 || starters >= 11) return DEFAULT_FORMATION[11];
  const size = TEAM_SIZES.find((s) => s <= Math.max(starters, 5)) ?? 11;
  return DEFAULT_FORMATION[size];
};

const lineRoles = (count: number, lineIndex: number, lineCount: number): string[] => {
  const isDefense = lineIndex === 0;
  const isAttack = lineIndex === lineCount - 1;
  if (isDefense) {
    if (count === 4) return ['LB', 'CB', 'CB', 'RB'];
    if (count === 5) return ['LWB', 'CB', 'CB', 'CB', 'RWB'];
    return Array(count).fill('CB');
  }
  if (isAttack) {
    if (count === 3) return ['LW', 'ST', 'RW'];
    return Array(count).fill('ST');
  }
  // Két középpályás sor esetén az alsó védekező, a felső támadó jellegű
  const twoMidLines = lineCount === 4;
  if (twoMidLines && lineIndex === 1) {
    if (count <= 2) return Array(count).fill('CDM');
  }
  if (twoMidLines && lineIndex === 2) {
    if (count <= 2) return Array(count).fill('CAM');
    if (count === 3) return ['LM', 'CAM', 'RM'];
  }
  if (count === 4) return ['LM', 'CM', 'CM', 'RM'];
  if (count === 5) return ['LM', 'CM', 'CM', 'CM', 'RM'];
  return Array(count).fill('CM');
};

const LINE_SPAN: Record<number, number> = { 1: 0, 2: 40, 3: 54, 4: 68, 5: 76, 6: 80 };

let slotCounter = 0;
export const newSlotId = () => {
  slotCounter += 1;
  return `slot-${slotCounter}`;
};

/** A felállás üres helyei, a kapustól előre haladva, soronként balról jobbra. */
export const buildFormationSlots = (formation: string): PitchSlot[] => {
  const lines = parseLines(formation) ?? parseLines(DEFAULT_FORMATION[11])!;
  const slots: PitchSlot[] = [{ id: newSlotId(), x: 50, y: 91, role: 'GK' }];
  const top = 20;
  const bottom = lines.length <= 2 ? 70 : 74;
  lines.forEach((count, i) => {
    const y = lines.length === 1 ? 50 : bottom - (i * (bottom - top)) / (lines.length - 1);
    const span = LINE_SPAN[count] ?? 84;
    const roles = lineRoles(count, i, lines.length);
    for (let k = 0; k < count; k++) {
      const x = count === 1 ? 50 : 50 - span / 2 + (k * span) / (count - 1);
      slots.push({ id: newSlotId(), x, y, role: roles[k] });
    }
  });
  return slots;
};

// Hátulról előre, balról jobbra – így a felállás cseréjekor a védők védők maradnak
const backToFront = (a: PitchSlot, b: PitchSlot) => (Math.abs(a.y - b.y) > 4 ? b.y - a.y : a.x - b.x);

/**
 * Új felállás alkalmazása: a jelenlegi kezdők hátulról előre sorban kerülnek az új
 * helyekre. Ha az új felállásban kevesebb hely van, a maradók a cserepadra mennek.
 */
export const applyFormation = (
  current: PitchSlot[],
  formation: string
): { slots: PitchSlot[]; benched: number[] } => applySlots(current, buildFormationSlots(formation));

/**
 * Tetszőleges helyek (pl. egy mentett saját felállás) alkalmazása: a kapus a kapus
 * helyére, a többiek hátulról előre sorban. Ami nem fér be, a cserepadra megy.
 */
export const applySlots = (
  current: PitchSlot[],
  targetSlots: PitchSlot[]
): { slots: PitchSlot[]; benched: number[] } => {
  const target = targetSlots.map((s) => ({ ...s, playerId: undefined })).sort(backToFront);
  const players = [...current].sort(backToFront).filter((s) => s.playerId != null).map((s) => s.playerId!);
  const keeper = current.find((s) => s.role === 'GK' && s.playerId != null)?.playerId;
  const keeperSlot = target.find((s) => s.role === 'GK');

  const queue = players.filter((p) => !(keeper != null && keeperSlot && p === keeper));
  const slots = target.map((slot) => {
    if (keeperSlot && slot === keeperSlot && keeper != null) return { ...slot, playerId: keeper };
    return { ...slot, playerId: queue.shift() };
  });
  return { slots, benched: queue };
};

export interface LineupPlacement {
  playerId: number;
  position?: string;
  pitchX?: number;
  pitchY?: number;
}

/**
 * A mentett kezdőkből a pálya helyei. Akinek van mentett helye, az a legközelebbi
 * felállás-helyre kerül (a saját koordinátáival); akinek nincs, az a még üres helyekre.
 */
export const slotsFromLineup = (formation: string | undefined | null, lineup: LineupPlacement[]): {
  formation: string;
  slots: PitchSlot[];
} => {
  // Saját (mentett) felállásnál a név nem számsor - ilyenkor az alaphelyek a létszámhoz
  // illő felállásból jönnek, a játékosok pedig a mentett koordinátáikra kerülnek
  const size = formationSize(formation);
  const name = formation || defaultFormationFor(lineup.length);
  const slots = buildFormationSlots(formation && size ? formation : defaultFormationFor(lineup.length));
  const free = new Set(slots.map((s) => s.id));

  const placed = lineup.filter((l) => l.pitchX != null && l.pitchY != null);
  const unplaced = lineup.filter((l) => l.pitchX == null || l.pitchY == null);

  placed.forEach((l) => {
    let best: PitchSlot | null = null;
    let bestDist = Infinity;
    slots.forEach((s) => {
      if (!free.has(s.id)) return;
      const d = Math.hypot(s.x - l.pitchX!, s.y - l.pitchY!);
      if (d < bestDist) {
        best = s;
        bestDist = d;
      }
    });
    const target = best as PitchSlot | null;
    if (target) {
      free.delete(target.id);
      target.x = l.pitchX!;
      target.y = l.pitchY!;
      target.role = l.position || target.role;
      target.playerId = l.playerId;
    } else {
      slots.push({ id: newSlotId(), x: l.pitchX!, y: l.pitchY!, role: l.position || 'CM', playerId: l.playerId });
    }
  });

  // Szerepkör szerint próbáljuk elhelyezni (pl. a kapus a kapus helyére), utána sorrendben
  const handled = new Set<number>();
  unplaced.forEach((l) => {
    if (!l.position) return;
    const match = slots.find((s) => free.has(s.id) && s.role === l.position);
    if (match) {
      free.delete(match.id);
      match.playerId = l.playerId;
      handled.add(l.playerId);
    }
  });
  const ordered = slots.filter((s) => free.has(s.id)).sort(backToFront);
  let extra = 0;
  unplaced
    .filter((l) => !handled.has(l.playerId))
    .forEach((l) => {
      const slot = ordered.shift();
      if (slot) {
        free.delete(slot.id);
        slot.playerId = l.playerId;
        if (l.position) slot.role = l.position;
      } else {
        // Több kezdő, mint ahány hely: középen egy sorba tesszük őket
        slots.push({ id: newSlotId(), x: 15 + ((extra * 17) % 70), y: 50, role: l.position || 'CM', playerId: l.playerId });
        extra++;
      }
    });

  return { formation: name, slots };
};

/** "Kovács Nimród" → "Kovács N." – a magyar névsorrendben a vezetéknév áll elöl. */
export const shortName = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1].charAt(0)}.`;
};
